import { useEffect, useState } from 'react'
import { T, CAT_LABELS } from '../lib/tokens'
import { Sidebar } from '../components/Sidebar'
import { Topbar } from '../components/Topbar'
import { LoadingSpinner } from '../components/ui'
import {
  useNewsSettings, useUpdateNewsSettings, useNewsProfile, useNewsArticles, useBanditArms,
  useCategories, NEWS_SETTINGS_DEFAULTS, type NewsSettings,
} from '../lib/hooks'
import { runNewsNow, fetchNewsSources, addNewsSource, updateNewsSource, deleteNewsSource, testNewsSource, type NewsSource } from '../lib/api'
import { supabase } from '../lib/supabase'
import { NewsFeedPanel } from './news-feed'

const WEEKDAY_LABELS = ['日', '月', '火', '水', '木', '金', '土']

export function NewsPage() {
  const [tab, setTab] = useState<'feed' | 'settings'>('feed')
  return (
    <div style={{ display:'flex',height:'100dvh',background:T.bg,color:T.text,fontFamily:T.font,overflow:'hidden' }}>
      <Sidebar />
      <div style={{ flex:1,display:'flex',flexDirection:'column',overflow:'hidden' }}>
        <Topbar />
        <main style={{ flex:1,overflowY:'auto',padding:'24px 28px 60px',maxWidth:880 }}>
          <div style={{ display:'flex',alignItems:'center',gap:8,marginBottom:18 }}>
            <h2 style={{ fontSize:18,fontWeight:700,margin:0 }}>ニュース</h2>
            <div style={{ display:'flex',gap:4,marginLeft:6 }}>
              <TabBtn active={tab==='feed'} onClick={() => setTab('feed')}>📰 一覧</TabBtn>
              <TabBtn active={tab==='settings'} onClick={() => setTab('settings')}>⚙ 設定</TabBtn>
            </div>
          </div>
          {tab === 'feed' ? <NewsFeedPanel /> : <NewsSettingsPanel />}
        </main>
      </div>
    </div>
  )
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick}
      style={{ padding:'5px 14px',borderRadius:6,border:`1px solid ${active?T.borderHi:T.border}`,background:active?T.bgActive:'transparent',color:active?T.text:T.textDim,fontSize:12,cursor:'pointer',fontFamily:T.font }}>
      {children}
    </button>
  )
}

function NewsSettingsPanel() {
  const { data: loaded, isLoading } = useNewsSettings()
  const { mutate: save, isPending: saving } = useUpdateNewsSettings()
  const { data: categories = [] } = useCategories()
  const [s, setS] = useState<NewsSettings>(NEWS_SETTINGS_DEFAULTS)
  const [running, setRunning] = useState(false)
  const [runMsg, setRunMsg] = useState<string | null>(null)

  useEffect(() => { if (loaded) setS(loaded) }, [loaded])

  function set<K extends keyof NewsSettings>(k: K, v: NewsSettings[K]) { setS(p => ({ ...p, [k]: v })) }
  function toggleWeekday(d: number) {
    set('weekdays', s.weekdays.includes(d) ? s.weekdays.filter(x => x !== d) : [...s.weekdays, d].sort())
  }
  function toggleExclude(cat: string) {
    set('exclude_categories', s.exclude_categories.includes(cat) ? s.exclude_categories.filter(c => c !== cat) : [...s.exclude_categories, cat])
  }

  async function handleRunNow() {
    setRunning(true); setRunMsg(null)
    try { await runNewsNow(); setRunMsg('✅ 配信を実行しました（数十秒後に届きます）') }
    catch { setRunMsg('⚠️ 実行に失敗しました') }
    finally { setRunning(false) }
  }

  if (isLoading) return <LoadingSpinner />
  return (
    <>
      <div style={{ display:'flex',marginBottom:16 }}>
        <button onClick={handleRunNow} disabled={running}
          style={{ marginLeft:'auto',padding:'7px 14px',borderRadius:6,border:`1px solid ${T.borderHi}`,background:T.bgActive,color:T.text,fontSize:12,cursor:running?'default':'pointer',fontFamily:T.font }}>
          {running ? '実行中…' : '⚡ 今すぐ配信'}
        </button>
      </div>
      {runMsg && <div style={{ fontSize:12,color:T.textDim,marginBottom:16 }}>{runMsg}</div>}
      <>
              <SourcesPanel />

              {/* 配信コントロール */}
              <Section title="配信">
                <Row label="配信を有効にする">
                  <Toggle on={s.enabled} onChange={v => set('enabled', v)} />
                </Row>
                <Row label="1日の配信件数">
                  <NumberInput value={s.count} min={1} max={10} onChange={v => set('count', v)} />
                </Row>
                <Row label="1回に収集するソース数（巡回）">
                  <NumberInput value={s.collect_per_run} min={1} max={15} onChange={v => set('collect_per_run', v)} />
                  <span style={{ fontSize:10,color:T.textMute }}>多いほど新鮮・Free枠に注意（目安≦{Math.max(1, Math.floor((37 - 3 * s.count) / 2))}）</span>
                </Row>
                <Row label="配信時刻（JST・0〜23時）">
                  <NumberInput value={s.delivery_hour} min={0} max={23} onChange={v => set('delivery_hour', v)} />
                  <span style={{ fontSize:11,color:T.textMute }}>{String(s.delivery_hour).padStart(2,'0')}:00 ごろ</span>
                </Row>
                <Row label="配信する曜日">
                  <div style={{ display:'flex',gap:6 }}>
                    {WEEKDAY_LABELS.map((w, d) => (
                      <button key={d} onClick={() => toggleWeekday(d)}
                        style={{ width:30,height:30,borderRadius:6,border:`1px solid ${s.weekdays.includes(d)?T.borderHi:T.border}`,background:s.weekdays.includes(d)?T.bgActive:'transparent',color:s.weekdays.includes(d)?T.text:T.textMute,fontSize:12,cursor:'pointer' }}>{w}</button>
                    ))}
                  </div>
                </Row>
                <div style={{ fontSize:10,color:T.textMute,marginTop:4 }}>※ 時刻変更は次回の配信時から反映されます</div>
              </Section>

              {/* 選別アルゴリズム */}
              <Section title="選別アルゴリズム">
                <Row label={`好み重視 ⇄ 発見重視（${s.exploration.toFixed(2)}）`}>
                  <input type="range" min={0} max={1} step={0.05} value={s.exploration} onChange={e => set('exploration', Number(e.target.value))} style={{ width:200,accentColor:T.accent }} />
                </Row>
                <Row label={`関連度重視 ⇄ 多様性重視（λ=${s.diversity.toFixed(2)}）`}>
                  <input type="range" min={0} max={1} step={0.05} value={s.diversity} onChange={e => set('diversity', Number(e.target.value))} style={{ width:200,accentColor:T.accent }} />
                </Row>
                <Row label="公開日フィルタ（0=無効・N日より古い記事を除外）">
                  <NumberInput value={s.date_filter_days} min={0} max={365} onChange={v => set('date_filter_days', v)} />
                </Row>
                <Row label="除外カテゴリ">
                  <div style={{ display:'flex',gap:6,flexWrap:'wrap' }}>
                    {categories.map(c => (
                      <button key={c.id} onClick={() => toggleExclude(c.id)}
                        style={{ padding:'3px 9px',borderRadius:5,border:`1px solid ${s.exclude_categories.includes(c.id)?'oklch(0.65 0.2 25)':T.border}`,background:s.exclude_categories.includes(c.id)?'oklch(0.65 0.2 25/0.15)':'transparent',color:s.exclude_categories.includes(c.id)?'oklch(0.75 0.15 25)':T.textDim,fontSize:11,cursor:'pointer',fontFamily:T.font }}>
                        {s.exclude_categories.includes(c.id) ? '✕ ' : ''}{c.label}
                      </button>
                    ))}
                  </div>
                </Row>
              </Section>

              {/* 後で読む・保持 */}
              <Section title="保持・リマインド">
                <Row label="未読の保持期間（日）"><NumberInput value={s.retention_days} min={1} max={90} onChange={v => set('retention_days', v)} /></Row>
                <Row label="リマインド初回（日後）"><NumberInput value={s.remind_initial_days} min={1} max={30} onChange={v => set('remind_initial_days', v)} /></Row>
                <Row label="リマインド間隔（日）"><NumberInput value={s.remind_repeat_days} min={1} max={30} onChange={v => set('remind_repeat_days', v)} /></Row>
                <Row label="リマインド最大回数"><NumberInput value={s.remind_max} min={0} max={5} onChange={v => set('remind_max', v)} /></Row>
              </Section>

              <button onClick={() => save(s)} disabled={saving}
                style={{ padding:'9px 20px',borderRadius:6,border:`1px solid ${T.borderHi}`,background:T.accentSoft,color:'oklch(0.85 0.12 258)',fontSize:13,fontWeight:600,cursor:saving?'default':'pointer',fontFamily:T.font,marginBottom:32 }}>
                {saving ? '保存中…' : '設定を保存'}
              </button>

              <ProfilePanel />
              <StatsPanel />
      </>
    </>
  )
}

function SourcesPanel() {
  const [sources, setSources] = useState<NewsSource[]>([])
  const [counts, setCounts] = useState<Record<string, number>>({})
  const [url, setUrl] = useState('')
  const [adding, setAdding] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [testing, setTesting] = useState<string | null>(null)
  const [preview, setPreview] = useState<Record<string, { title: string; url: string }[]>>({})
  const [editing, setEditing] = useState<string | null>(null)   // 表示名を編集中のソースid
  const [draft, setDraft] = useState('')

  // 表示名(site_name)の編集を確定。配信メッセージの「📍 ソース名」に反映される。
  async function saveName(s: NewsSource) {
    const name = draft.trim()
    setEditing(null)
    if (!name || name === s.site_name) return
    setSources(list => list.map(x => x.id === s.id ? { ...x, site_name: name } : x))
    await updateNewsSource(s.id, { site_name: name }).catch(() => {})
  }

  async function test(s: NewsSource) {
    setTesting(s.id)
    try { const r = await testNewsSource(s.id); setPreview(p => ({ ...p, [s.id]: r.items ?? [] })) }
    catch { setPreview(p => ({ ...p, [s.id]: [] })) }
    finally { setTesting(null) }
  }

  // 並び替え（上下）: 配列を入れ替え、各ソースの sort_order を新しい位置で永続化
  function move(idx: number, dir: -1 | 1) {
    const j = idx + dir
    if (j < 0 || j >= sources.length) return
    const next = [...sources]
    ;[next[idx], next[j]] = [next[j], next[idx]]
    setSources(next)
    next.forEach((s, i) => { updateNewsSource(s.id, { sort_order: i }).catch(() => {}) })
  }

  useEffect(() => {
    fetchNewsSources().then(r => setSources(r.sources ?? [])).catch(() => setError('読み込みに失敗しました'))
    supabase.from('ml_news_articles').select('source_id').then(({ data }) => {
      const c: Record<string, number> = {}
      for (const r of data ?? []) { const k = (r as any).source_id as string | null; if (k) c[k] = (c[k] ?? 0) + 1 }
      setCounts(c)
    })
  }, [])

  async function handleAdd() {
    const top = url.trim()
    if (!/^https?:\/\//.test(top)) { setError('http(s):// で始まるURLを入力してください'); return }
    setAdding(true); setError(null)
    try { const { source } = await addNewsSource(top); setSources(s => [source, ...s]); setUrl('') }
    catch { setError('登録に失敗しました') }
    finally { setAdding(false) }
  }
  async function toggle(s: NewsSource) {
    setSources(list => list.map(x => x.id === s.id ? { ...x, is_active: !x.is_active } : x))
    await updateNewsSource(s.id, { is_active: !s.is_active }).catch(() => {})
  }
  async function remove(s: NewsSource) {
    setSources(list => list.filter(x => x.id !== s.id))
    await deleteNewsSource(s.id).catch(() => {})
  }

  return (
    <Section title="ニュースソース">
      <div style={{ display:'flex',gap:8,marginBottom:12 }}>
        <input type="text" value={url} placeholder="https://example.com（監視するサイトのトップURL）"
          onChange={e => setUrl(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') handleAdd() }}
          style={{ flex:1,padding:'7px 10px',borderRadius:6,border:`1px solid ${T.border}`,background:T.bg,color:T.text,fontSize:12,fontFamily:T.font }} />
        <button onClick={handleAdd} disabled={adding}
          style={{ padding:'7px 16px',borderRadius:6,border:`1px solid ${T.borderHi}`,background:T.bgActive,color:T.text,fontSize:12,cursor:adding?'default':'pointer',fontFamily:T.font }}>
          {adding ? '…' : '追加'}
        </button>
      </div>
      {error && <div style={{ fontSize:11,color:'oklch(0.7 0.2 25)',marginBottom:8 }}>{error}</div>}

      <div style={{ display:'flex',flexDirection:'column',gap:6 }}>
        {sources.length === 0 && <div style={{ fontSize:12,color:T.textMute }}>ソース未登録</div>}
        {sources.map((s, idx) => (
          <div key={s.id} style={{ padding:'8px 10px',borderRadius:6,border:`1px solid ${T.border}`,opacity:s.is_active?1:0.5 }}>
            <div style={{ display:'flex',alignItems:'center',gap:10 }}>
              <div style={{ display:'flex',flexDirection:'column' }}>
                <button onClick={() => move(idx, -1)} disabled={idx===0} title="上へ" style={{ background:'transparent',border:'none',cursor:idx===0?'default':'pointer',color:idx===0?T.border:T.textMute,fontSize:10,lineHeight:1,padding:0 }}>▲</button>
                <button onClick={() => move(idx, 1)} disabled={idx===sources.length-1} title="下へ" style={{ background:'transparent',border:'none',cursor:idx===sources.length-1?'default':'pointer',color:idx===sources.length-1?T.border:T.textMute,fontSize:10,lineHeight:1,padding:0 }}>▼</button>
              </div>
              {editing === s.id ? (
                <div style={{ flex:1,minWidth:0,display:'flex',flexDirection:'column',gap:2 }}>
                  <input autoFocus value={draft} onChange={e => setDraft(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') saveName(s); else if (e.key === 'Escape') setEditing(null) }}
                    onBlur={() => saveName(s)}
                    placeholder="配信に表示するソース名"
                    style={{ width:'100%',padding:'3px 6px',borderRadius:5,border:`1px solid ${T.borderHi}`,background:T.bg,color:T.text,fontSize:12,fontFamily:T.font }} />
                  <div style={{ fontSize:9,fontFamily:T.fontMono,color:T.textMute,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis' }}>{s.top_url}</div>
                </div>
              ) : (
                <a href={s.top_url} target="_blank" rel="noreferrer" style={{ flex:1,minWidth:0,textDecoration:'none' }}>
                  <div style={{ fontSize:12,color:T.text,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis' }}>{s.site_name || s.top_url}</div>
                  <div style={{ fontSize:9,fontFamily:T.fontMono,color:T.textMute,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis' }}>{s.top_url}</div>
                </a>
              )}
              {editing !== s.id && (
                <button onClick={() => { setEditing(s.id); setDraft(s.site_name ?? '') }} title="表示名を編集" style={{ background:'transparent',border:'none',cursor:'pointer',fontSize:13,lineHeight:1,color:T.text }}>✎</button>
              )}
              <span style={{ fontSize:10,fontFamily:T.fontMono,color:s.fetch_type==='rss'?'oklch(0.72 0.14 150)':'oklch(0.72 0.14 60)' }}>
                {s.fetch_type === 'rss' ? '📡 RSS' : '🔎 Tavily'}
              </span>
              <span title="配信実績" style={{ fontSize:10,fontFamily:T.fontMono,color:T.textMute }}>配信 {counts[s.id] ?? 0}</span>
              <button onClick={() => test(s)} disabled={testing===s.id} title="今このソースから取れる記事を確認" style={{ padding:'3px 8px',borderRadius:5,border:`1px solid ${T.border}`,background:'transparent',color:T.textDim,fontSize:10,cursor:'pointer',fontFamily:T.font }}>
                {testing===s.id ? '取得中…' : 'テスト取得'}
              </button>
              <button onClick={() => toggle(s)} title={s.is_active ? '無効化' : '有効化'} style={{ background:'transparent',border:'none',cursor:'pointer',fontSize:14,color:s.is_active?T.accent:T.textMute }}>
                {s.is_active ? '◉' : '○'}
              </button>
              <button onClick={() => remove(s)} title="削除" style={{ background:'transparent',border:'none',cursor:'pointer',fontSize:14,color:T.textDim }}>×</button>
            </div>
            {preview[s.id] && (
              <div style={{ marginTop:8,paddingTop:8,borderTop:`1px solid ${T.border}`,display:'flex',flexDirection:'column',gap:3 }}>
                {preview[s.id].length === 0 && <div style={{ fontSize:11,color:T.textMute }}>取得できませんでした（RSS/JS描画/権限などの可能性）</div>}
                {preview[s.id].map((it, i) => (
                  <a key={i} href={it.url} target="_blank" rel="noreferrer" style={{ fontSize:11,color:T.textDim,textDecoration:'none',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis' }}>・{it.title}</a>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </Section>
  )
}

function ProfilePanel() {
  const { data } = useNewsProfile()
  const p = data?.profile_json as any
  return (
    <Section title="興味関心プロファイル（自動学習）">
      {!p ? <div style={{ fontSize:12,color:T.textMute }}>まだ学習データがありません（高評価・お気に入り・深掘りで学習されます）</div> : (
        <div style={{ display:'flex',flexDirection:'column',gap:10,fontSize:12 }}>
          <ProfileLine label="関心" items={p.interests} />
          <ProfileLine label="好む論調" items={p.preferred_tone} />
          <ProfileLine label="除外トピック" items={p.excluded_topics} danger />
          <ProfileLine label="キーワード" items={p.keywords} />
          {data?.updated_at && <div style={{ fontSize:10,color:T.textMute,fontFamily:T.fontMono }}>更新: {new Date(data.updated_at).toLocaleString('ja-JP')}</div>}
        </div>
      )}
    </Section>
  )
}

function ProfileLine({ label, items, danger }: { label: string; items?: string[]; danger?: boolean }) {
  if (!Array.isArray(items) || items.length === 0) return null
  return (
    <div style={{ display:'flex',gap:8,alignItems:'flex-start' }}>
      <span style={{ minWidth:88,color:T.textMute,fontSize:11 }}>{label}</span>
      <div style={{ display:'flex',gap:6,flexWrap:'wrap' }}>
        {items.map((t, i) => (
          <span key={i} style={{ padding:'2px 8px',borderRadius:4,background:danger?'oklch(0.65 0.2 25/0.12)':T.bgActive,color:danger?'oklch(0.75 0.15 25)':T.textDim,fontSize:11 }}>{t}</span>
        ))}
      </div>
    </div>
  )
}

function StatsPanel() {
  const { data: arms = [] } = useBanditArms()
  const { data: articles = [] } = useNewsArticles(150)

  const delivered = articles.length
  const favorites = articles.filter(a => a.is_favorite).length
  const detailed  = articles.filter(a => a.is_detailed).length
  const readLater = articles.filter(a => a.read_later).length

  return (
    <Section title="統計・学習状況">
      <div style={{ display:'flex',gap:10,marginBottom:16,flexWrap:'wrap' }}>
        <Stat label="配信(直近)" value={delivered} />
        <Stat label="お気に入り" value={favorites} />
        <Stat label="深掘り" value={detailed} />
        <Stat label="後で読む" value={readLater} />
      </div>

      {arms.length > 0 && (
        <div style={{ marginBottom:16 }}>
          <div style={{ fontSize:10,fontFamily:T.fontMono,color:T.textMute,marginBottom:8,letterSpacing:'0.1em' }}>カテゴリ別エンゲージ率（reward/pulls）</div>
          {arms.map(a => {
            const rate = a.pulls > 0 ? a.rewards / a.pulls : 0
            return (
              <div key={a.cat_id} style={{ display:'flex',alignItems:'center',gap:8,marginBottom:4 }}>
                <span style={{ minWidth:90,fontSize:11,color:T.textDim }}>{CAT_LABELS[a.cat_id] ?? a.cat_id}</span>
                <div style={{ flex:1,maxWidth:200,height:4,background:T.border,borderRadius:2,overflow:'hidden' }}>
                  <div style={{ width:`${Math.round(rate*100)}%`,height:'100%',background:T.accent }} />
                </div>
                <span style={{ fontSize:10,fontFamily:T.fontMono,color:T.textMute }}>{Math.round(rate*100)}% ({a.pulls})</span>
              </div>
            )
          })}
        </div>
      )}

      {/* 記事リストは「一覧」タブへ集約したため、ここでは統計のみ表示する。 */}
    </Section>
  )
}

// ── 小物 ──────────────────────────────────────────────────
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom:28,padding:16,border:`1px solid ${T.border}`,borderRadius:10,background:T.bgRaised }}>
      <div style={{ fontSize:13,fontWeight:600,marginBottom:14 }}>{title}</div>
      {children}
    </section>
  )
}
function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display:'flex',alignItems:'center',gap:12,marginBottom:12,flexWrap:'wrap' }}>
      <span style={{ minWidth:200,fontSize:12,color:T.textDim }}>{label}</span>
      {children}
    </div>
  )
}
function Toggle({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }) {
  return (
    <button onClick={() => onChange(!on)} style={{ width:44,height:24,borderRadius:12,border:'none',background:on?T.accent:T.border,position:'relative',cursor:'pointer' }}>
      <span style={{ position:'absolute',top:2,left:on?22:2,width:20,height:20,borderRadius:10,background:'#fff',transition:'left 0.15s' }} />
    </button>
  )
}
function NumberInput({ value, min, max, onChange }: { value: number; min: number; max: number; onChange: (v: number) => void }) {
  return (
    <input type="number" value={value} min={min} max={max}
      onChange={e => onChange(Math.max(min, Math.min(max, Number(e.target.value) || 0)))}
      style={{ width:72,padding:'5px 8px',borderRadius:6,border:`1px solid ${T.border}`,background:T.bg,color:T.text,fontSize:12,fontFamily:T.fontMono }} />
  )
}
function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div style={{ background:T.bgPanel,border:`1px solid ${T.border}`,borderRadius:6,padding:'8px 14px',minWidth:84 }}>
      <div style={{ fontSize:9,fontFamily:T.fontMono,color:T.textMute,marginBottom:3 }}>{label}</div>
      <div style={{ fontSize:20,fontWeight:700,fontFamily:T.fontMono,color:T.text }}>{value}</div>
    </div>
  )
}
